import {
  ACCOUNT_STATE_LABELS,
  EMPLOYEE_STATUS_LABELS,
  type Employee,
  type EmployeeDirectoryResponse,
  type ProjectTeamResponse,
  type TemplateOption,
  type InvitationResponse,
  employeeDirectoryResponseSchema,
  employeeSchema,
  invitationResponseSchema,
  projectTeamResponseSchema,
} from '@sivan/shared';
import { askConfirm } from '../ui/confirm';
import { useId, useState } from 'react';
import {
  SELF_DEACTIVATION_NOTE,
  SELF_LABEL,
  SELF_REMOVAL_NOTE,
  SELF_ROLES_NOTE,
  accessSafetyMessage,
} from './self-access';
import { ApiError, NetworkError, apiDelete, apiGet, apiPost, apiPut } from '../api/client';
import { useCurrentProject } from '../current-project';
import { LoadingBlock, Problem } from '../handover/common';
import { Modal } from '../master-data/Modal';
import { Value } from '../master-data/Value';
import { AccessStep } from './AccessStep';
import { InvitationLink, invitationLabel } from './InvitationLink';
import { useLoad, useWorkspace } from '../master-data/use-master-data';

// צוות והרשאות: the project's team (members, responsibilities, project roles) and, for the
// business Super Admin, the organization's employee directory. Adding an employee never grants
// sign-in: that needs a verified identity of an active account. In the hosted deployment the
// Super Admin sends the sign-in invitation from here; elsewhere it is set up outside this screen.

function explain(error: unknown): string {
  if (error instanceof ApiError) {
    const fields = (error.body['fields'] ?? {}) as Record<string, string>;
    const codes = Object.values(fields);
    if (codes.includes('beyond_scope'))
      return 'אין באפשרותך להעניק או להסיר הרשאה שאינה בתחום ההרשאות שלך בפרויקט.';
    if (codes.includes('duplicate')) return 'כתובת הדוא"ל כבר שייכת לעובד אחר.';
    if (codes.includes('inactive')) return 'העובד אינו פעיל, ולכן לא ניתן להעניק לו הרשאות.';
    if (codes.includes('not_member')) return 'יש לצרף את העובד לצוות הפרויקט קודם.';
    const conflict = error.body['conflict'] as { code?: string } | undefined;
    if (conflict?.code === 'already_member') return 'העובד כבר חבר בצוות הפרויקט.';
    const safety = conflict?.code === undefined ? undefined : accessSafetyMessage(conflict.code);
    if (safety !== undefined) return safety;
    if (error.status === 409) return 'הנתונים עודכנו במקום אחר. טענו מחדש ונסו שוב.';
    if (error.status === 403) return 'אין לך הרשאה לפעולה זו.';
    if (error.status === 422) return 'חלק מהערכים אינם תקינים. בדקו ונסו שוב.';
  }
  if (error instanceof NetworkError) return 'אין חיבור לשרת. הפעולה לא בוצעה; נסו שוב.';
  return 'הפעולה נכשלה. נסו שוב.';
}

function AccountChip({
  account,
  invitation,
  invitations = false,
}: {
  readonly account: Employee['account'];
  readonly invitation?: Employee['invitation'];
  /** Invitations are made from this screen (hosted deployment): show their state. */
  readonly invitations?: boolean;
}) {
  const label = invitations && account.state === 'pending' ? invitationLabel(invitation) : null;
  const invited = invitations && account.state === 'pending' && account.identityVerified;
  const closed = invitation?.state === 'expired' || invitation?.state === 'revoked';
  return (
    <span
      className={`chip chip--account-${account.state}${closed ? ' chip--warning' : ''}`}
      title={account.identityVerified ? 'זהות מאומתת' : 'אין זהות מאומתת'}
    >
      {label ?? (invited ? 'הוזמן, ממתין לכניסה ראשונה' : ACCOUNT_STATE_LABELS[account.state])}
    </span>
  );
}

/** A sign-in invitation can go to this employee (active, with a work address, no active account). */
const invitable = (e: Employee): boolean =>
  e.status === 'active' &&
  e.workEmail !== null &&
  e.account.state !== 'active' &&
  e.account.state !== 'deactivated';

/** Checkboxes of role templates; templates beyond the caller's scope cannot be changed. */
function RolesForm({
  templates,
  current,
  self = false,
  onSave,
  onCancel,
}: {
  readonly templates: readonly TemplateOption[];
  readonly current: readonly string[];
  /** The signed-in user's own roles: removing any asks for an explicit confirmation. */
  readonly self?: boolean;
  readonly onSave: (ids: string[]) => Promise<void>;
  readonly onCancel: () => void;
}) {
  const [chosen, setChosen] = useState<string[]>([...current]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="roles-form"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        onSave(chosen).then(
          () => {
            setBusy(false);
          },
          (failure: unknown) => {
            setBusy(false);
            setError(explain(failure));
          },
        );
      }}
    >
      {self && <p className="notice notice--warning">{SELF_ROLES_NOTE}</p>}
      <fieldset className="roles-form__list">
        <legend>תפקידים והרשאות</legend>
        {templates.map((t) => (
          <label key={t.id} className={`role-option${t.grantable ? '' : ' role-option--locked'}`}>
            <input
              type="checkbox"
              checked={chosen.includes(t.id)}
              disabled={!t.grantable || (self && current.includes(t.id))}
              onChange={(e) => {
                setChosen((list) =>
                  e.target.checked ? [...list, t.id] : list.filter((id) => id !== t.id),
                );
              }}
            />
            <span className="role-option__name">{t.name}</span>
            <span className="hint">{t.permissions.length} הרשאות</span>
            {!t.grantable && <span className="hint">מחוץ לתחום ההרשאות שלך</span>}
          </label>
        ))}
      </fieldset>
      <p className="hint">
        ניתן להעניק רק תפקיד שכל הרשאותיו נמצאות גם אצלך. מתן הרשאות אינו מאפשר כניסה למערכת בלי
        חשבון עם זהות מאומתת.
      </p>
      {error !== null && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'שומר…' : 'שמירת ההרשאות'}
        </button>
        <button type="button" className="button" onClick={onCancel}>
          ביטול
        </button>
      </div>
    </form>
  );
}

export function ProjectTeam({ projectId }: { readonly projectId: string }) {
  const [version, setVersion] = useState(0);
  const [data, reload] = useLoad(`team:${projectId}:${String(version)}`, () =>
    apiGet(`/projects/${projectId}/team`, projectTeamResponseSchema),
  );
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState({ employeeId: '', responsibility: '' });
  const [message, setMessage] = useState<{ kind: 'error' | 'done'; text: string } | null>(null);
  const titleId = useId();
  if (data.state === 'loading') return <LoadingBlock />;
  if (data.state === 'forbidden') return <Problem text="אין לך הרשאה לצפות בצוות של פרויקט זה." />;
  if (data.state === 'error')
    return <Problem text="טעינת הצוות נכשלה." requestId={data.requestId} onRetry={reload} />;
  const team: ProjectTeamResponse = data.data;
  const manage = team.permissions.manage;
  const run = async (action: () => Promise<unknown>, done: string) => {
    setMessage(null);
    try {
      await action();
      setMessage({ kind: 'done', text: done });
      setEditing(null);
      setVersion((v) => v + 1);
    } catch (error) {
      setMessage({ kind: 'error', text: explain(error) });
      throw error;
    }
  };
  const member = team.members.find((m) => m.employee.id === editing);
  return (
    <section className="team" aria-labelledby={titleId}>
      <h2 id={titleId} className="section-title">
        צוות הפרויקט <span className="count">({team.members.length})</span>
      </h2>
      {message !== null && (
        <p
          className={`notice ${message.kind === 'error' ? 'notice--error' : 'notice--success'}`}
          role={message.kind === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      )}
      {team.members.length === 0 ? (
        <p className="empty">אין עובדים בצוות הפרויקט.</p>
      ) : (
        <ul className="member-list">
          {team.members.map((m) => (
            <li
              key={m.employee.id}
              className={`member${m.employee.status === 'inactive' ? ' member--inactive' : ''}`}
              aria-label={m.employee.fullName}
            >
              <div className="member__main">
                <p className="member__name">
                  {m.employee.fullName}
                  {m.employee.isSelf && <span className="chip chip--self">{SELF_LABEL}</span>}
                </p>
                <p className="hint">{m.responsibility}</p>
                <p className="member__contact">
                  {m.employee.phone !== null && <Value text={m.employee.phone} />}
                  {m.employee.workEmail !== null && <Value text={m.employee.workEmail} />}
                </p>
              </div>
              <div className="member__state">
                {m.employee.status === 'inactive' && (
                  <span className="chip chip--error">{EMPLOYEE_STATUS_LABELS.inactive}</span>
                )}
                <AccountChip account={m.employee.account} />
                {m.roles.length === 0 ? (
                  <span className="hint">ללא הרשאות בפרויקט</span>
                ) : (
                  m.roles.map((r) => (
                    <span key={r.templateId} className="chip chip--role">
                      {r.name}
                    </span>
                  ))
                )}
              </div>
              {manage && (
                <div className="member__actions">
                  <button
                    type="button"
                    className="button button--small"
                    aria-label={`עריכת הרשאות: ${m.employee.fullName}`}
                    onClick={() => {
                      setEditing(m.employee.id);
                      setMessage(null);
                    }}
                  >
                    הרשאות
                  </button>
                  {m.employee.isSelf ? (
                    <span className="hint">{SELF_REMOVAL_NOTE}</span>
                  ) : (
                    <button
                      type="button"
                      className="button button--small button--danger"
                      aria-label={`הסרה מהצוות: ${m.employee.fullName}`}
                      onClick={() => {
                        void askConfirm({
                          title: `להסיר את ${m.employee.fullName} מצוות הפרויקט?`,
                          message: 'גם ההרשאות שלו בפרויקט יוסרו.',
                          confirmLabel: 'הסרה מהצוות',
                          danger: true,
                        }).then((yes) => {
                          if (yes)
                            void run(
                              () => apiDelete(`/projects/${projectId}/team/${m.employee.id}`),
                              'העובד הוסר מהצוות.',
                            ).catch(() => undefined);
                        });
                      }}
                    >
                      הסרה
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {manage && (
        <form
          className="member-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (adding.employeeId === '' || adding.responsibility.trim().length < 2) return;
            void run(
              () =>
                apiPost(
                  `/projects/${projectId}/team`,
                  { employeeId: adding.employeeId, responsibility: adding.responsibility.trim() },
                  projectTeamResponseSchema,
                ),
              'העובד צורף לצוות. ניתן כעת להעניק לו הרשאות.',
            ).then(
              () => {
                setAdding({ employeeId: '', responsibility: '' });
              },
              () => undefined,
            );
          }}
        >
          <label className="field">
            <span>צירוף עובד לצוות</span>
            <select
              value={adding.employeeId}
              onChange={(e) => {
                setAdding((a) => ({ ...a, employeeId: e.target.value }));
              }}
            >
              <option value="">
                {team.candidates.length === 0 ? 'אין עובדים פעילים להוספה' : 'בחירת עובד'}
              </option>
              {team.candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.fullName}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>תחום אחריות (לתצוגה בלבד)</span>
            <input
              value={adding.responsibility}
              maxLength={80}
              placeholder="למשל: אחראי בניין A"
              onChange={(e) => {
                setAdding((a) => ({ ...a, responsibility: e.target.value }));
              }}
            />
          </label>
          <button
            type="submit"
            className="button button--primary"
            disabled={adding.employeeId === '' || adding.responsibility.trim().length < 2}
          >
            + צירוף לצוות
          </button>
        </form>
      )}
      <Modal
        title={member === undefined ? 'הרשאות' : `הרשאות בפרויקט: ${member.employee.fullName}`}
        open={member !== undefined}
        onClose={() => {
          setEditing(null);
        }}
      >
        {member !== undefined && (
          <RolesForm
            templates={team.templates.filter((t) =>
              t.permissions.every((p) => !p.startsWith('organization.')),
            )}
            current={member.roles.map((r) => r.templateId)}
            self={member.employee.isSelf}
            onCancel={() => {
              setEditing(null);
            }}
            onSave={(ids) =>
              run(
                () =>
                  apiPut(
                    `/projects/${projectId}/team/${member.employee.id}/roles`,
                    { templateIds: ids },
                    projectTeamResponseSchema,
                  ),
                'ההרשאות עודכנו.',
              )
            }
          />
        )}
      </Modal>
    </section>
  );
}

function EmployeeForm({
  employee,
  onSave,
  onCancel,
}: {
  readonly employee: Employee | null;
  readonly onSave: (values: {
    fullName: string;
    workEmail: string;
    phone: string;
    status: 'active' | 'inactive';
  }) => Promise<void>;
  readonly onCancel: () => void;
}) {
  const [values, setValues] = useState({
    fullName: employee?.fullName ?? '',
    workEmail: employee?.workEmail ?? '',
    phone: employee?.phone ?? '',
    status: employee?.status ?? ('active' as const),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="employee-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (values.fullName.trim().length < 2) {
          setError('יש להזין שם מלא.');
          return;
        }

        setBusy(true);
        setError(null);
        onSave(values).then(
          () => {
            setBusy(false);
          },
          (failure: unknown) => {
            setBusy(false);
            setError(explain(failure));
          },
        );
      }}
    >
      <label className="field">
        <span>שם מלא</span>
        <input
          value={values.fullName}
          maxLength={120}
          onChange={(e) => {
            setValues((v) => ({ ...v, fullName: e.target.value }));
          }}
        />
      </label>
      <label className="field">
        <span>דוא"ל עבודה (רשות)</span>
        <input
          type="email"
          dir="ltr"
          value={values.workEmail}
          onChange={(e) => {
            setValues((v) => ({ ...v, workEmail: e.target.value }));
          }}
        />
      </label>
      <label className="field">
        <span>טלפון (רשות)</span>
        <input
          type="tel"
          dir="ltr"
          value={values.phone}
          onChange={(e) => {
            setValues((v) => ({ ...v, phone: e.target.value }));
          }}
        />
      </label>
      {employee?.isSelf === true && <p className="hint">{SELF_DEACTIVATION_NOTE}</p>}
      {employee !== null && (
        <label className="field">
          <span>סטטוס</span>
          <select
            value={values.status}
            onChange={(e) => {
              setValues((v) => ({ ...v, status: e.target.value as 'active' | 'inactive' }));
            }}
          >
            <option value="active">{EMPLOYEE_STATUS_LABELS.active}</option>
            <option value="inactive" disabled={employee.isSelf && employee.status === 'active'}>
              {EMPLOYEE_STATUS_LABELS.inactive}
            </option>
          </select>
        </label>
      )}
      {values.status === 'inactive' && employee?.status === 'active' && (
        <p className="notice notice--warning">
          עובד לא פעיל מאבד מיד את הגישה למערכת ולא ניתן לשבץ אותו למסירות.
        </p>
      )}
      <p className="hint">
        הוספת עובד אינה מאפשרת כניסה למערכת. כניסה דורשת חשבון עם זהות מאומתת והרשאות בפרויקט.
      </p>
      {error !== null && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'שומר…' : employee === null ? 'הוספת העובד' : 'שמירה'}
        </button>
        <button type="button" className="button" onClick={onCancel}>
          ביטול
        </button>
      </div>
    </form>
  );
}

function Directory({
  data,
  onChanged,
}: {
  readonly data: EmployeeDirectoryResponse;
  readonly onChanged: () => void;
}) {
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<
    | { kind: 'new' }
    | { kind: 'edit' | 'roles'; id: string }
    // Adding an employee, after the details: access, then the sign-in link.
    | { kind: 'access' | 'link'; employee: Employee }
    | null
  >(null);
  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  const [link, setLink] = useState<InvitationResponse | null>(null);
  const titleId = useId();
  const needle = query.trim().toLowerCase();
  const invitations = data.permissions.invite === true;
  const act = (e: Employee, run: () => Promise<void>) => {
    setSending(e.id);
    setMessage(null);
    setProblem(null);
    run()
      .catch((error: unknown) => {
        setProblem(explain(error));
      })
      .finally(() => {
        setSending(null);
      });
  };
  const invite = (e: Employee) => {
    act(e, async () => {
      setLink(await apiPost(`/employees/${e.id}/invitation`, {}, invitationResponseSchema));
      onChanged();
    });
  };
  const revoke = (e: Employee) => {
    act(e, async () => {
      const yes = await askConfirm({
        title: `לבטל את קישור ההזמנה של ${e.fullName}?`,
        message: 'לא ניתן יהיה להשתמש בקישור. אפשר ליצור קישור חדש בכל עת.',
        confirmLabel: 'ביטול ההזמנה',
        danger: true,
      });
      if (!yes) return;
      await apiDelete(`/employees/${e.id}/invitation`);
      setMessage(`קישור ההזמנה של ${e.fullName} בוטל.`);
      onChanged();
    });
  };
  const items = data.items.filter(
    (e) =>
      needle === '' ||
      [e.fullName, e.workEmail ?? '', e.phone ?? ''].some((v) => v.toLowerCase().includes(needle)),
  );
  const current =
    dialog !== null && (dialog.kind === 'edit' || dialog.kind === 'roles')
      ? (data.items.find((e) => e.id === dialog.id) ?? null)
      : null;
  const orNull = (v: string) => (v.trim() === '' ? null : v.trim());
  return (
    <section className="directory" aria-labelledby={titleId}>
      <h2 id={titleId} className="section-title">
        כל העובדים <span className="count">({data.items.length})</span>
      </h2>
      <div className="directory__toolbar">
        <label className="field">
          <span>חיפוש עובד</span>
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
          />
        </label>
        {data.permissions.manage && (
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              setDialog({ kind: 'new' });
            }}
          >
            + עובד חדש
          </button>
        )}
      </div>
      {message !== null && (
        <p className="notice notice--success" role="status">
          {message}
        </p>
      )}
      {problem !== null && (
        <p className="notice notice--error" role="alert">
          {problem}
        </p>
      )}
      {items.length === 0 ? (
        <p className="empty">לא נמצאו עובדים.</p>
      ) : (
        <ul className="member-list">
          {items.map((e) => (
            <li
              key={e.id}
              className={`member${e.status === 'inactive' ? ' member--inactive' : ''}`}
              aria-label={e.fullName}
            >
              <div className="member__main">
                <p className="member__name">
                  {e.fullName}
                  {e.isSelf && <span className="chip chip--self">{SELF_LABEL}</span>}
                </p>
                <p className="member__contact">
                  {e.phone !== null && <Value text={e.phone} />}
                  {e.workEmail !== null && <Value text={e.workEmail} />}
                </p>
                <p className="hint">
                  {e.memberships.length === 0
                    ? 'אינו חבר בצוות של פרויקט'
                    : e.memberships
                        .map(
                          (m) =>
                            `${m.projectName}: ${m.roles.map((r) => r.name).join(', ') || 'ללא הרשאות'}`,
                        )
                        .join(' · ')}
                </p>
              </div>
              <div className="member__state">
                <span className={`chip${e.status === 'inactive' ? ' chip--error' : ''}`}>
                  {EMPLOYEE_STATUS_LABELS[e.status]}
                </span>
                <AccountChip
                  account={e.account}
                  invitation={e.invitation}
                  invitations={invitations}
                />
                {e.organizationRoles.map((r) => (
                  <span key={r.templateId} className="chip chip--role">
                    {r.name} (כל הארגון)
                  </span>
                ))}
              </div>
              {data.permissions.manage && (
                <div className="member__actions">
                  <button
                    type="button"
                    className="button button--small"
                    aria-label={`עריכת עובד: ${e.fullName}`}
                    onClick={() => {
                      setDialog({ kind: 'edit', id: e.id });
                    }}
                  >
                    עריכה
                  </button>
                  <button
                    type="button"
                    className="button button--small"
                    aria-label={`תפקיד ארגוני: ${e.fullName}`}
                    onClick={() => {
                      setDialog({ kind: 'roles', id: e.id });
                    }}
                  >
                    תפקיד ארגוני
                  </button>
                  {invitations && invitable(e) && (
                    <button
                      type="button"
                      className="button button--small"
                      disabled={sending !== null}
                      aria-label={`${e.invitation == null ? 'יצירת קישור הזמנה' : 'קישור הזמנה חדש'}: ${e.fullName}`}
                      onClick={() => {
                        invite(e);
                      }}
                    >
                      {sending === e.id
                        ? 'יוצר…'
                        : e.invitation == null
                          ? 'יצירת קישור הזמנה'
                          : 'קישור חדש'}
                    </button>
                  )}
                  {invitations &&
                    e.invitation?.state === 'pending' &&
                    e.account.state === 'pending' && (
                      <button
                        type="button"
                        className="button button--small"
                        disabled={sending !== null}
                        aria-label={`ביטול ההזמנה: ${e.fullName}`}
                        onClick={() => {
                          revoke(e);
                        }}
                      >
                        ביטול ההזמנה
                      </button>
                    )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <Modal
        title={
          dialog?.kind === 'new' || dialog?.kind === 'access' || dialog?.kind === 'link'
            ? 'עובד חדש'
            : dialog?.kind === 'roles'
              ? `תפקיד ארגוני: ${current?.fullName ?? ''}`
              : `עריכת עובד: ${current?.fullName ?? ''}`
        }
        open={dialog !== null}
        onClose={() => {
          setDialog(null);
        }}
      >
        {(dialog?.kind === 'new' || dialog?.kind === 'access' || dialog?.kind === 'link') && (
          <WizardSteps current={dialog.kind} invite={invitations} />
        )}
        {dialog?.kind === 'access' && (
          <AccessStep
            employee={dialog.employee}
            explain={explain}
            onDone={(summary) => {
              setMessage(summary);
              setDialog({ kind: 'link', employee: dialog.employee });
              onChanged();
            }}
            onSkip={() => {
              setDialog({ kind: 'link', employee: dialog.employee });
            }}
          />
        )}
        {dialog?.kind === 'link' && (
          <div className="wizard-step">
            {invitations ? (
              <>
                <p>
                  נשאר לשלוח ל{dialog.employee.fullName} קישור כניסה אישי. הקישור מוצג פעם אחת
                  להעתקה, ובתוקף 24 שעות.
                </p>
                {dialog.employee.workEmail === null && (
                  <p className="notice notice--warning">
                    לעובד אין דוא״ל עבודה. כדי ליצור קישור כניסה יש להוסיף לו דוא״ל בעריכת הפרטים.
                  </p>
                )}
              </>
            ) : (
              <p>
                העובד נוסף. חשבון הכניסה שלו מוגדר בשירות הזהות של הארגון; הגישה בפרויקטים נקבעת
                כאן, במסך צוות הפרויקט.
              </p>
            )}
            <div className="form-actions">
              {invitations && dialog.employee.workEmail !== null && (
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => {
                    const employee = dialog.employee;
                    setDialog(null);
                    invite(employee);
                  }}
                >
                  יצירת קישור כניסה
                </button>
              )}
              <button
                type="button"
                className="button"
                onClick={() => {
                  setDialog(null);
                }}
              >
                סיום
              </button>
            </div>
          </div>
        )}
        {dialog?.kind === 'roles' && current !== null && (
          <RolesForm
            templates={data.templates}
            current={current.organizationRoles.map((r) => r.templateId)}
            self={current.isSelf}
            onCancel={() => {
              setDialog(null);
            }}
            onSave={async (ids) => {
              await apiPut(
                `/employees/${current.id}/organization-roles`,
                { templateIds: ids },
                employeeSchema,
              );
              setDialog(null);
              setMessage('התפקיד הארגוני עודכן.');
              onChanged();
            }}
          />
        )}
        {(dialog?.kind === 'new' || (dialog?.kind === 'edit' && current !== null)) && (
          <EmployeeForm
            employee={dialog.kind === 'edit' ? current : null}
            onCancel={() => {
              setDialog(null);
            }}
            onSave={async (values) => {
              const body = {
                fullName: values.fullName.trim(),
                workEmail: orNull(values.workEmail),
                phone: orNull(values.phone),
              };
              if (dialog.kind === 'edit' && current !== null)
                await apiPut(
                  `/employees/${current.id}`,
                  { ...body, rowVersion: current.rowVersion, status: values.status },
                  employeeSchema,
                );
              else {
                // Details saved: the wizard continues with access, then the sign-in link.
                const created = await apiPost('/employees', body, employeeSchema);
                setMessage(`${created.fullName} נוסף/ה לעובדי הארגון.`);
                setDialog({ kind: 'access', employee: created });
                onChanged();
                return;
              }
              setDialog(null);
              setMessage('פרטי העובד נשמרו.');
              onChanged();
            }}
          />
        )}
      </Modal>
      <Modal
        title={`קישור הזמנה: ${link?.employee.fullName ?? ''}`}
        open={link !== null}
        onClose={() => {
          setLink(null);
        }}
      >
        {link !== null && (
          <InvitationLink
            invitation={link}
            onClose={() => {
              setLink(null);
            }}
          />
        )}
      </Modal>
    </section>
  );
}

function OrganizationDirectory() {
  const [version, setVersion] = useState(0);
  const [data, reload] = useLoad(`employees:${String(version)}`, () =>
    apiGet('/employees', employeeDirectoryResponseSchema),
  );
  // After a change the list reloads in place, so its confirmation stays visible.
  const [last, setLast] = useState<EmployeeDirectoryResponse | null>(null);
  if (data.state === 'ready' && last !== data.data) setLast(data.data);
  if (data.state === 'loading' && last !== null)
    return (
      <Directory
        data={last}
        onChanged={() => {
          setVersion((v) => v + 1);
        }}
      />
    );
  if (data.state === 'loading') return <LoadingBlock />;
  if (data.state === 'forbidden')
    return <p className="notice">רשימת כל העובדים זמינה למנהלי הארגון בלבד.</p>;
  if (data.state === 'error')
    return (
      <Problem text="טעינת רשימת העובדים נכשלה." requestId={data.requestId} onRetry={reload} />
    );
  return (
    <Directory
      data={data.data}
      onChanged={() => {
        setVersion((v) => v + 1);
      }}
    />
  );
}

/** צוות והרשאות. */
export function TeamScreen() {
  const [workspace, reload] = useWorkspace();
  const project = useCurrentProject(workspace.state === 'ready' ? workspace.data.projects : []);
  const [tab, setTab] = useState<'project' | 'all'>('project');
  const id = useId();
  if (workspace.state === 'loading') return <LoadingBlock />;
  if (workspace.state === 'forbidden') return <Problem text="אין לך הרשאה לצפות בצוות." />;
  if (workspace.state === 'error')
    return (
      <Problem text="טעינת הפרויקטים נכשלה." requestId={workspace.requestId} onRetry={reload} />
    );
  return (
    <div className="team-screen">
      <div className="tabs" role="tablist" aria-label="צוות">
        {(['project', 'all'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            id={`${id}-${t}`}
            aria-selected={tab === t}
            aria-controls={`${id}-panel`}
            className="tabs__tab"
            onClick={() => {
              setTab(t);
            }}
          >
            {t === 'project' ? 'צוות הפרויקט' : 'כל העובדים'}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${tab}`}>
        {tab === 'all' ? (
          <OrganizationDirectory />
        ) : project === undefined ? (
          <p className="empty">אין פרויקטים זמינים עבורך.</p>
        ) : (
          <ProjectTeam key={project.id} projectId={project.id} />
        )}
      </div>
    </div>
  );
}

/** Where the user is in adding an employee: details, access, sign-in link. */
function WizardSteps({
  current,
  invite,
}: {
  readonly current: 'new' | 'access' | 'link';
  readonly invite: boolean;
}) {
  const steps = [
    { id: 'new', label: 'פרטים' },
    { id: 'access', label: 'גישה' },
    { id: 'link', label: invite ? 'קישור כניסה' : 'סיום' },
  ] as const;
  const at = steps.findIndex((s) => s.id === current);
  return (
    <ol className="wizard-steps" aria-label="שלבי הוספת עובד">
      {steps.map((s, index) => (
        <li
          key={s.id}
          className={`wizard-steps__step${index < at ? ' wizard-steps__step--done' : ''}`}
          aria-current={index === at ? 'step' : undefined}
        >
          <span aria-hidden="true">{index < at ? '✓' : String(index + 1)}</span> {s.label}
        </li>
      ))}
    </ol>
  );
}
